import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, Loader, User, Building2, MessageCircleMore, CheckCircle2, XCircle, Clock, Mail, MailOpen, Bell } from 'lucide-react';
import claimsService from '../../services/claimService';
import signalRService from '../../services/SignalRService';

const styles = {
    primaryColor: '#F76707',
    secondaryColor: '#0A6EBD',
    lightOrange: '#FFF4E6',
};

/**
 * Composant d'affichage et d'ajout de commentaires amélioré pour l'interface client
 * @param {Array} comments - Liste des commentaires 
 * @param {Function} onAddComment - Fonction pour ajouter un commentaire
 * @param {Boolean} isSubmitting - Indique si un commentaire est en cours d'envoi
 * @param {String} trackingNumber - Numéro de suivi de la réclamation
 */
const CommentsSection = ({ comments = [], onAddComment, isSubmitting, trackingNumber }) => {
    const [newComment, setNewComment] = useState('');
    const [formattedComments, setFormattedComments] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const commentsEndRef = useRef(null);
    const signalRSetupDone = useRef(false);
    const [notificationSound] = useState(new Audio('/notification-sound.mp3'));
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        console.log("CommentsSection initialisé avec trackingNumber:", trackingNumber);
    }, [trackingNumber]);

    // Fonction pour normaliser le format d'un commentaire
    const normalizeComment = useCallback((comment) => {
        const isClient =
            comment.author === 'Client' ||
            comment.authorRole === 'Client' ||
            comment.role === 'Client';

        const isAgent =
            comment.author === 'Agent' ||
            comment.authorRole === 'backoffice' ||
            comment.role === 'bank' ||
            comment.authorRole === 'agent';

        // Ne pas afficher les commentaires internes dans l'interface client
        if (comment.isInternal) {
            return null;
        }

        // Récupérer le nom réel de l'auteur
        const authorName = comment.author || comment.authorName || 'Inconnu';

        return {
            id: comment.id || `temp-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
            hasTemporaryId: !comment.id,
            author: isClient ? 'Vous' : authorName,
            role: isClient ? 'Client' : 'Agent',
            text: comment.text || comment.content || comment.message,
            date: comment.date || comment.createdAt || new Date(),
            isClient: isClient,
            isAgent: isAgent,
            isReadByClient: !!comment.isReadByClient,
            isReadByBank: !!comment.isReadByBank
        };
    }, []);

    // Formater les commentaires pour l'affichage
    useEffect(() => {
        if (Array.isArray(comments)) {
            const formatted = comments
                .map(comment => normalizeComment(comment))
                .filter(comment => comment !== null); // Filtrer les commentaires null (internes)

            setFormattedComments(formatted);
            setTotalCount(formatted.length);
        }
    }, [comments, normalizeComment]);

    // Configurer SignalR pour recevoir les mises à jour en temps réel
    // Modification du composant CommentsSection.jsx pour être compatible avec le backend

    // Calculer le nombre de messages non lus
    const calculateUnreadCount = useCallback(() => {
        if (!Array.isArray(formattedComments) || formattedComments.length === 0) {
            setUnreadCount(0);
            return;
        }

        // Pour le client, compter les messages non lus de l'agent
        const unread = formattedComments.filter(comment => {
            if (!comment || !comment.id || comment.hasTemporaryId) return false;
            return comment.isAgent && !comment.isReadByClient;
        }).length;

        setUnreadCount(unread);
    }, [formattedComments]);

    // Dans le composant CommentsSection - Modifier la partie qui configure SignalR
    useEffect(() => {
        if (!trackingNumber || signalRSetupDone.current) return;

        console.log("Configuration de SignalR avec trackingNumber:", trackingNumber);

        const setupSignalR = async () => {
            try {
                // Rejoindre le groupe de la réclamation
                await signalRService.joinClaimGroup(trackingNumber);
                console.log("Groupe SignalR rejoint pour", trackingNumber);

                // Callback pour les nouveaux commentaires - nom de méthode corrigé
                const cleanupNewComment = signalRService.onNewComment((newComment) => {
                    console.log('Nouveau commentaire reçu via SignalR:', newComment);

                    // Vérifier que le commentaire a un ID valide
                    if (!newComment || !newComment.id) {
                        console.warn('Commentaire reçu sans ID valide, ignoré:', newComment);
                        return;
                    }

                    // Ignorer les commentaires internes pour les clients
                    if (newComment.isInternal) {
                        return;
                    }

                    // Adapter le format des données reçues au format attendu par normalizeComment
                    const commentToNormalize = {
                        id: newComment.id,
                        content: newComment.content, // ou 'message' selon votre modèle
                        authorName: newComment.authorName,
                        authorRole: newComment.authorRole,
                        createdAt: newComment.createdAt,
                        isInternal: newComment.isInternal,
                        isReadByClient: newComment.isReadByClient,
                        isReadByBank: newComment.isReadByBank
                    };

                    // Normaliser le commentaire reçu via SignalR
                    const normalizedComment = normalizeComment(commentToNormalize);
                    if (!normalizedComment) return; // Si c'est un commentaire interne filtré

                    // Mettre à jour l'état des commentaires sans dupliquer
                    setFormattedComments(prev => {
                        // Vérifier si le commentaire existe déjà avec son ID
                        const existsById = prev.some(c => c.id === normalizedComment.id);
                        if (existsById) {
                            console.log(`Commentaire avec ID ${normalizedComment.id} déjà présent, ignoré`);
                            return prev;
                        }

                        // Jouer le son de notification pour les messages entrants
                        try {
                            notificationSound.play().catch(e => console.log("Son de notification non supporté:", e));
                        } catch (e) {
                            console.log("Erreur lors de la lecture du son:", e);
                        }

                        // Retourner la liste mise à jour
                        const newList = [...prev, normalizedComment];
                        setTotalCount(newList.length);
                        calculateUnreadCount();
                        return newList;
                    });
                });

                // Callback pour les statuts de lecture - nom de méthode corrigé
                const cleanupReadStatus = signalRService.onCommentReadStatus((readStatus) => {
                    console.log('Statut de lecture mis à jour via SignalR:', readStatus);

                    if (!readStatus || !readStatus.id) {
                        console.warn('Statut de lecture sans ID reçu, ignoré');
                        return;
                    }

                    setFormattedComments(prev => {
                        return prev.map(comment => {
                            if (comment.id === readStatus.id) {
                                console.log(`Mise à jour du statut de lecture pour le commentaire ${comment.id}`);
                                return {
                                    ...comment,
                                    isReadByClient: readStatus.isReadByClient,
                                    isReadByBank: readStatus.isReadByBank
                                };
                            }
                            return comment;
                        });
                    });

                    // Recalculer le nombre de messages non lus après mise à jour
                    calculateUnreadCount();
                });

                // Callback pour les changements de statut (nouveau)
                const cleanupStatusChange = signalRService.onStatusChange((statusData) => {
                    console.log('Statut de la réclamation mis à jour:', statusData);
                    // Gérer la mise à jour du statut si nécessaire
                    // Vous pouvez utiliser un callback ou émettre un événement ici
                });

                signalRSetupDone.current = true;

                // Nettoyage lors du démontage
                return () => {
                    cleanupNewComment?.();
                    cleanupReadStatus?.();
                    cleanupStatusChange?.();
                };
            } catch (error) {
                console.error('Erreur lors de l\'initialisation de SignalR:', error);
                // Réessayer après un délai
                setTimeout(setupSignalR, 3000);
            }
        };

        setupSignalR();

        // Nettoyage lors du démontage du composant
        return () => {
            if (trackingNumber) {
                signalRService.leaveClaimGroup(trackingNumber);
            }
            signalRSetupDone.current = false;
        };
    }, [trackingNumber, normalizeComment, notificationSound, calculateUnreadCount]);


    // Marquer les messages comme lus
    const markNewMessagesAsRead = useCallback(async () => {
        if (!trackingNumber || !Array.isArray(formattedComments)) {
            console.log("SORTIE ANTICIPÉE: données manquantes");
            console.log({ trackingNumber, formattedCommentsCount: formattedComments?.length });
            return;
        }

        console.log("CLIENT: Tentative de marquer comme lu", {
            trackingNumber,
            commentIds: formattedComments
                .filter(c => c.isAgent && !c.isReadByClient && c.id)
                .map(c => c.id)
        });

        try {
            // Filtrer les messages de l'agent non lus avec des IDs valides
            const unreadMessages = formattedComments.filter(comment =>
                comment.isAgent && !comment.isReadByClient && comment.id && !comment.hasTemporaryId
            );

            if (unreadMessages.length === 0) {
                console.log('Aucun message non lu à marquer');
                return;
            }

            console.log(`Marquage de ${unreadMessages.length} messages comme lus`);

            for (const comment of unreadMessages) {
                try {
                    console.log(`Tentative de marquer le message ${comment.id} comme lu...`);
                    const result = await claimsService.markCommentAsRead(
                        trackingNumber,
                        comment.id,
                        'client'
                    );

                    console.log(`Résultat pour commentaire ${comment.id}:`, result);

                    // Mise à jour locale
                    setFormattedComments(prev =>
                        prev.map(c => c.id === comment.id ? { ...c, isReadByClient: true } : c)
                    );
                } catch (error) {
                    console.error(`Erreur lors du marquage du commentaire ${comment.id}:`, error);
                }
            }

            // Recalculer le nombre de messages non lus
            calculateUnreadCount();
        } catch (error) {
            console.error("Erreur générale:", error);
        }
    }, [trackingNumber, formattedComments, calculateUnreadCount]);

    // Mise à jour du nombre de messages non lus
    useEffect(() => {
        calculateUnreadCount();
    }, [formattedComments, calculateUnreadCount]);

    // Marquer les messages comme lus quand on ouvre les commentaires
    useEffect(() => {
        if (expanded && unreadCount > 0) {
            console.log("Tentative de marquage des messages comme lus car expanded=true et unreadCount=", unreadCount);
            markNewMessagesAsRead();
        }
    }, [expanded, unreadCount, markNewMessagesAsRead]);

    // Faire défiler automatiquement vers le dernier message
    useEffect(() => {
        if (expanded && commentsEndRef.current) {
            commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [formattedComments, expanded]);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            console.error('Erreur formatage date:', e);
            return dateString;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (newComment.trim() && !isSubmitting) {
            try {
                await onAddComment(newComment);
                setNewComment(''); // Réinitialiser le champ uniquement si l'ajout a réussi

                // Faire défiler vers le bas après l'ajout du commentaire
                setTimeout(() => {
                    if (commentsEndRef.current) {
                        commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
                    }
                }, 100);
            } catch (error) {
                // L'erreur sera gérée par le composant parent
                console.error('Erreur lors de l\'envoi du commentaire:', error);
            }
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const toggleExpanded = () => {
        setExpanded(!expanded);
    };

    return (
        <div className="comments-section mb-4 border rounded shadow-sm">
            {/* En-tête des commentaires avec compteur et toggle */}
            <div
                className="d-flex align-items-center justify-content-between p-3 border-bottom"
                style={{
                    backgroundColor: styles.secondaryColor,
                    color: 'white',
                    cursor: 'pointer'
                }}
                onClick={toggleExpanded}
            >
                <div className="d-flex align-items-center gap-2">
                    <MessageSquare size={20} />
                    <h5 className="mb-0">Commentaires</h5>
                </div>
                <div className="d-flex align-items-center gap-2">
                    {totalCount > 0 && (
                        <span className="badge bg-light text-dark">{totalCount}</span>
                    )}
                    {unreadCount > 0 && (
                        <span className="badge bg-danger">{unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}</span>
                    )}
                    {expanded ? (
                        <XCircle size={20} />
                    ) : (
                        <MessageCircleMore size={20} />
                    )}
                </div>
            </div>

            {/* Bouton "Marquer comme lu" visible lorsque section repliée */}
            {!expanded && unreadCount > 0 && (
                <div className="d-flex justify-content-end p-2 border-bottom bg-light">
                    <button
                        className="btn btn-sm btn-danger"
                        onClick={(e) => {
                            e.stopPropagation();
                            markNewMessagesAsRead();
                        }}
                    >
                        <Mail size={14} className="me-1" />
                        Marquer comme lu ({unreadCount})
                    </button>
                </div>
            )}

            {/* Corps des commentaires */}
            {expanded && (
                <>
                    {/* Légende des statuts de message */}
                    <MessageLegend />

                    <div className="p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {formattedComments.length === 0 ? (
                            <div className="text-center p-4 bg-light rounded">
                                <p className="text-muted mb-0">Aucun commentaire pour le moment</p>
                            </div>
                        ) : (
                            formattedComments.map((comment, index) => (
                                <MessageComponent key={index} comment={comment} index={index} />
                            ))
                        )}
                        <div ref={commentsEndRef} />
                    </div>

                    <div className="p-3 border-top">
                        <form onSubmit={handleSubmit} className="d-flex gap-2">
                            <input
                                type="text"
                                className="form-control"
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Ajouter un commentaire..."
                                disabled={isSubmitting}
                            />
                            <button
                                type="submit"
                                className="btn d-flex align-items-center justify-content-center"
                                style={{
                                    backgroundColor: styles.primaryColor,
                                    color: 'white',
                                    opacity: isSubmitting || !newComment.trim() ? 0.7 : 1,
                                    width: '44px',
                                    height: '38px'
                                }}
                                disabled={isSubmitting || !newComment.trim()}
                            >
                                {isSubmitting ? (
                                    <Loader className="animate-spin" size={20} />
                                ) : (
                                    <Send size={20} />
                                )}
                            </button>
                        </form>
                        <div className="text-muted small mt-1">
                            <em>Appuyez sur Entrée pour envoyer</em>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Composant de message individuel à intégrer dans le CommentsSection client
// Composant de message individuel corrigé
const MessageComponent = ({ comment, index }) => {
    const isUnread = !comment.hasTemporaryId && (
        comment.isAgent && !comment.isReadByClient
    );

    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return new Intl.DateTimeFormat('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            console.error('Erreur formatage date:', e);
            return dateString;
        }
    };

    return (
        <div
            key={index}
            className={`p-3 rounded mb-3 ${comment.isClient ? 'ms-auto' : 'me-auto'}`}
            style={{
                backgroundColor: comment.isAgent ? styles.secondaryColor : styles.lightOrange,
                color: comment.isAgent ? 'white' : 'inherit',
                border: isUnread
                    ? '2px solid #dc3545'
                    : (comment.isAgent ? 'none' : `1px solid ${styles.primaryColor}`),
                maxWidth: '85%',
                marginLeft: comment.isClient ? 'auto' : '0',
                marginRight: comment.isClient ? '0' : 'auto',
                borderRadius: comment.isClient
                    ? '15px 0px 15px 15px'
                    : '0px 15px 15px 15px',
                boxShadow: isUnread ? '0 0 5px rgba(220, 53, 69, 0.5)' : 'none'
            }}
        >
            <div className="d-flex justify-content-between mb-2 align-items-center">
                <span className="fw-bold d-flex align-items-center gap-1">
                    {comment.isClient
                        ? <User size={16} />
                        : <Building2 size={16} />}
                    {comment.author}
                </span>
                <span className="text-small fst-italic" style={{
                    fontSize: '0.8rem',
                    opacity: 0.8
                }}>
                    {formatDate(comment.date)}
                </span>
            </div>
            <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</p>

            {/* Statut de lecture avec correction pour assurer la visibilité */}
            <div className="d-flex justify-content-end mt-2 align-items-center">
                {comment.isClient ? (
                    <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                        style={{
                            backgroundColor: comment.isReadByBank ? 'rgba(25, 135, 84, 0.15)' : 'rgba(255, 193, 7, 0.15)',
                            color: comment.isReadByBank ? '#198754' : '#ffc107',
                            fontWeight: 'bold',  // Rendre le texte plus visible
                            fontSize: '0.85rem'   // Taille de texte légèrement plus grande
                        }}>
                        {comment.isReadByBank ? (
                            <>
                                <CheckCircle2 size={16} /> {/* Légèrement plus grand */}
                                <span className="ms-1">Lu par la banque</span>
                            </>
                        ) : (
                            <>
                                <Clock size={16} />
                                <span className="ms-1">En attente de lecture</span>
                            </>
                        )}
                    </div>
                ) : (
                    comment.hasTemporaryId ? (
                        <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                            style={{
                                backgroundColor: 'rgba(108, 117, 125, 0.15)',
                                color: '#6c757d',
                                fontWeight: 'bold',
                                fontSize: '0.85rem'
                            }}>
                            <Loader size={16} className="animate-spin" />
                            <span className="ms-1">En cours d'envoi</span>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                            style={{
                                backgroundColor: comment.isReadByClient ? 'rgba(25, 135, 84, 0.15)' : 'rgba(220, 53, 69, 0.15)',
                                color: comment.isReadByClient ? '#198754' : '#dc3545',
                                fontWeight: 'bold',
                                fontSize: '0.85rem'
                            }}>
                            {comment.isReadByClient ? (
                                <>
                                    <CheckCircle2 size={16} />
                                    <span className="ms-1">Lu</span>
                                </>
                            ) : (
                                <>
                                    <Bell size={16} />
                                    <span className="ms-1">Non lu</span>
                                </>
                            )}
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

// Légende des messages corrigée
const MessageLegend = () => {
    return (
        <div className="message-legend p-2 mb-3 border-top bg-light" style={{ zIndex: 1000, position: 'relative' }}>
            <h6 className="mb-2 fw-bold text-secondary">Statuts des messages :</h6>
            <div className="d-flex flex-wrap gap-2">
                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                    style={{
                        backgroundColor: 'rgba(25, 135, 84, 0.15)',
                        color: '#198754',
                        fontWeight: 'bold',
                        fontSize: '0.85rem'
                    }}>
                    <CheckCircle2 size={16} />
                    <span className="ms-1">Lu</span>
                </div>
                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                    style={{
                        backgroundColor: 'rgba(255, 193, 7, 0.15)',
                        color: '#ffc107',
                        fontWeight: 'bold',
                        fontSize: '0.85rem'
                    }}>
                    <Clock size={16} />
                    <span className="ms-1">En attente de lecture</span>
                </div>
                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded-pill"
                    style={{
                        backgroundColor: 'rgba(220, 53, 69, 0.15)',
                        color: '#dc3545',
                        fontWeight: 'bold',
                        fontSize: '0.85rem'
                    }}>
                    <Bell size={16} />
                    <span className="ms-1">Nouveau message</span>
                </div>
            </div>
        </div>
    );
};

export default CommentsSection;